import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  styled,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import { EmployeeApi } from "../../../system/api/EmployeeApi";
import { ErrorHandler } from "../../../system/ApiService";
import AlertModal from "../../Common/AlertModal";
import { useLoadingDispatch } from "../../../system/context/LoadingContext";
import { ModalStyle } from "../../../styles/theme";
import {
  PRIMARY,
  GRAYPALEACCENT,
  GRAYPALEBACKGROUND1,
  GRAYPALEBORDER,
  GRAYPALECONTENTS,
  GRAYPALEDISABLED,
  GRAYPALETITLE,
  GREENCONTENTS,
  ORANGECONTENTS,
  REDTITLE,
} from "../../../styles/color";
import {
  AdminEmployee,
  AdminEmployeeFlag,
  AdminEmployeeManaged,
  AdminEmployeeQuery,
  AdminEmployeeStatus,
  AdminEmployeeSummary,
  Page,
  SyncDryRun,
  SyncResult,
} from "../../../system/types/AdminEmployee";
import { Employee } from "../../../system/types/Employee";
import { InitEmployee } from "../../../system/types/initObject";
import AddEmployee from "./AddEmployee";

/**
 * 관리자 직원 관리 — 전 직원 목록.
 *
 * 핵심은 "관리" 열이다. Directory 전환 뒤 직원의 90% 는 매일 자동으로 덮이므로,
 * 관리자가 어느 행을 고쳐도 되는지(수동) 어느 행은 고쳐 봤자 사라지는지(자동)가 보여야 한다.
 *
 * 화면 규칙: 색은 상태 점 하나에만 쓴다. 행 액션은 모든 행에 똑같이 둘 — 활성 스위치, 수정 연필.
 * 안 되는 건 숨기지 않고 비활성으로 두고 이유를 툴팁에 쓴다 (행마다 버튼 수가 달라지면 난잡하다).
 */

const PAGE_SIZE = 50;
const SORTABLE = ["name", "workplace", "jobPosition"] as const;
type SortField = (typeof SORTABLE)[number];

const STATUS_LABEL: Record<AdminEmployeeStatus, string> = {
  ACTIVE: "활성",
  INACTIVE: "비활성",
  RESIGNED: "퇴사",
  ALL: "전체",
};
const MANAGED_LABEL: Record<AdminEmployeeManaged, string> = {
  ALL: "전체",
  AUTO: "자동",
  MANUAL: "수동",
};
/** 관리자가 읽는 말. "Directory" 같은 시스템 이름은 쓰지 않는다 */
const FLAG_LABEL: Record<AdminEmployeeFlag, string> = {
  MISSING_IN_SOURCE: "인사 명단에 없음",
  TYPE_MISMATCH: "소속 확인 필요",
  NO_JOB_POSITION: "직급 없음",
};
const FLAG_HELP: Record<AdminEmployeeFlag, string> = {
  MISSING_IN_SOURCE: "활성 상태인데 portal 인사 명단에 없는 직원. 다음 동기화 때 퇴사 처리됩니다",
  TYPE_MISMATCH: "본사/지사 구분이 인사 명단과 다른 직원. 지사 직원 수정에서 정정하세요",
  NO_JOB_POSITION: "직급이 비어 있어 임원 판별·정렬이 안 되는 직원",
};
const FLAG_TONE: Record<AdminEmployeeFlag, { bg: string; fg: string }> = {
  MISSING_IN_SOURCE: { bg: "#FFF0F3", fg: "#F25C75" },
  TYPE_MISMATCH: { bg: "#FFF9C4", fg: "#F59210" },
  NO_JOB_POSITION: { bg: "#F0F2F5", fg: "#718093" },
};

const initialQuery: AdminEmployeeQuery = {
  keyword: "",
  workplace: "",
  status: "ACTIVE",
  managed: "ALL",
  flag: undefined,
  page: 0,
  size: PAGE_SIZE,
  sort: "name,asc",
};

function toEmployee(row: AdminEmployee): Employee {
  return {
    id: row.id,
    email: row.email,
    name: row.name,
    workplace: row.workplace,
    phone: row.phone ?? "",
    department: row.department ?? "",
    team: row.team ?? "",
    jobTitle: row.jobTitle ?? "",
    jobPosition: row.jobPosition ?? "",
    type: row.type,
    active: row.active,
    isResignation: row.isResignation,
  };
}

function formatDateTime(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getMonth() + 1}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EmployeeList() {
  const Loading = useLoadingDispatch();
  const [query, setQuery] = useState<AdminEmployeeQuery>(initialQuery);
  const [keywordInput, setKeywordInput] = useState("");
  const [page, setPage] = useState<Page<AdminEmployee>>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    number: 0,
    size: PAGE_SIZE,
  });
  const [summary, setSummary] = useState<AdminEmployeeSummary | null>(null);

  // 지사 직원 추가·수정 모달 (기존 AddEmployee 재사용)
  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [employeeData, setEmployeeData] = useState<Employee>(InitEmployee);
  const handleClose = () => {
    setEmployeeData(InitEmployee);
    setOpen(false);
  };

  const fetchSummary = useCallback(() => {
    EmployeeApi.getAdminSummary()
      .then((res) => setSummary(res.data))
      .catch((err) => AlertModal("msg", ErrorHandler(err)));
  }, []);

  const fetchList = useCallback(() => {
    Loading({ type: "LOADING" });
    EmployeeApi.getAdminEmployees(query)
      .then((res) => setPage(res.data))
      .catch((err) => AlertModal("msg", ErrorHandler(err)))
      .finally(() => Loading({ type: "COMPLETE" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const refresh = () => {
    fetchSummary();
    fetchList();
  };

  // ── 필터 ─────────────────────────────────────────────
  const patch = (partial: Partial<AdminEmployeeQuery>) =>
    setQuery((q) => ({ ...q, ...partial, page: 0 }));

  const submitKeyword = () => patch({ keyword: keywordInput.trim() });

  const toggleFlag = (flag: AdminEmployeeFlag) =>
    patch({ flag: query.flag === flag ? undefined : flag });

  const [sortField, sortDir] = query.sort.split(",") as [SortField, "asc" | "desc"];
  const onSort = (field: SortField) => {
    const dir = sortField === field && sortDir === "asc" ? "desc" : "asc";
    setQuery((q) => ({ ...q, sort: `${field},${dir}`, page: 0 }));
  };

  // ── 동기화 ───────────────────────────────────────────
  const runDryRun = () => {
    Loading({ type: "LOADING" });
    EmployeeApi.syncDryRun()
      .then((res) => {
        const r: SyncDryRun = res.data;
        const p = r.planned;
        const lines = [
          `신규 ${p.toCreate} · 갱신 ${p.toUpdate} · 복직 ${p.toReactivate} · 퇴사 ${p.toResign} · 변동없음 ${p.unchanged}`,
          `인사 명단에 없는 활성 ${p.activeButMissingInSource} · 건너뜀 ${p.skippedNew} · 소속 확인 필요 ${p.typeMismatch}`,
        ];
        if (r.guard.blocked) {
          AlertModal("err", "지금 실행하면 차단됩니다", [...lines, "", ...r.guard.violations].join("\n"));
        } else {
          AlertModal("msg", "예행 결과 (DB 는 바뀌지 않았습니다)", lines.join("\n"));
        }
        fetchSummary();
      })
      .catch((err) => AlertModal("err", ErrorHandler(err)))
      .finally(() => Loading({ type: "COMPLETE" }));
  };

  const runSync = () => {
    Swal.fire({
      title: "지금 동기화할까요?",
      text: "인사 명단에 없는 직원은 퇴사 처리되어 바로 접근이 막힙니다. 먼저 예행으로 확인하세요.",
      icon: "question",
      confirmButtonText: "동기화",
      cancelButtonText: "취소",
      showCancelButton: true,
      confirmButtonColor: GREENCONTENTS,
      cancelButtonColor: REDTITLE,
    }).then((result) => {
      if (!result.isConfirmed) return;
      Loading({ type: "LOADING" });
      EmployeeApi.syncRun()
        .then((res) => {
          const r: SyncResult = res.data;
          AlertModal("success", "동기화 완료", r.message);
          refresh();
        })
        .catch((err) => {
          // 409 = 가드 차단. 아무것도 저장되지 않았다.
          AlertModal("err", "동기화가 차단되었습니다", ErrorHandler(err));
          fetchSummary();
        })
        .finally(() => Loading({ type: "COMPLETE" }));
    });
  };

  // ── 행 액션 ──────────────────────────────────────────
  const toggleActive = (row: AdminEmployee) => {
    const next = row.active ? "비활성화" : "활성화";
    const warn = row.managed && !row.active
      ? " 자동 관리 직원은 다음 동기화에서 인사 명단 상태를 따릅니다."
      : "";
    Swal.fire({
      title: `${row.name} 님을 ${next}할까요?`,
      text: (row.active ? "비활성화되면 로그인해도 예약 화면에 접근할 수 없습니다." : "") + warn,
      icon: "question",
      confirmButtonText: next,
      cancelButtonText: "취소",
      showCancelButton: true,
      confirmButtonColor: GREENCONTENTS,
      cancelButtonColor: REDTITLE,
    }).then((result) => {
      if (!result.isConfirmed) return;
      EmployeeApi.updateEmployee(toEmployee(row))
        .then(() => refresh())
        .catch((err) => AlertModal("msg", ErrorHandler(err)));
    });
  };

  const openAdd = () => {
    setModalType("추가");
    setEmployeeData(InitEmployee);
    setOpen(true);
  };

  const openEdit = (row: AdminEmployee) => {
    setModalType("수정");
    setEmployeeData(toEmployee(row));
    setOpen(true);
  };

  // ── 렌더 ─────────────────────────────────────────────
  // 인원 많은 근무지가 위로. 본사·천안공장·헬스케어·코르트 순이 자연스럽다.
  const workplacesByCount = [...(summary?.workplaces ?? [])].sort((a, b) => b.count - a.count);
  const lastRunText = summary?.lastRun
    ? `마지막 동기화 ${summary.lastRun.kind === "SYNC" ? "실행" : "예행"} ${formatDateTime(summary.lastRun.at)}` +
      (summary.lastRun.blocked ? " · 차단됨" : "")
    : "마지막 동기화 없음";

  return (
    <Grid container item xs={12} direction="column" sx={{ mt: 2 }}>
      {/* 제목 + 요약 한 줄 + 동기화 */}
      <Row sx={{ mb: 2 }}>
        <div>
          <span style={{ fontSize: 15, fontWeight: 500, color: GRAYPALEACCENT }}>직원</span>
          {summary && (
            <Muted as="span" style={{ marginLeft: 12 }}>
              전체 {summary.total.toLocaleString()} · 활성 {summary.active.toLocaleString()} · 자동{" "}
              {summary.managedActive.toLocaleString()} · 수동 {summary.manualActive.toLocaleString()}
            </Muted>
          )}
        </div>
        <Group>
          <Tooltip title={summary?.lastRun?.message ?? ""}>
            <Muted>{lastRunText}</Muted>
          </Tooltip>
          <Tooltip title="아무것도 바꾸지 않고, 지금 동기화하면 몇 명이 바뀌는지만 봅니다">
            <WhiteButton onClick={runDryRun}>예행</WhiteButton>
          </Tooltip>
          <GreenButton onClick={runSync}>동기화</GreenButton>
        </Group>
      </Row>

      {/* 필터 한 줄 — 높이 34 통일 */}
      <Row sx={{ gap: 1, mb: 1.25 }}>
        <OutlinedInput
          size="small"
          placeholder="이름, 이메일, 사번"
          value={keywordInput}
          onChange={(e) => setKeywordInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submitKeyword()}
          autoComplete="new-password"
          sx={{ ...control, width: 230 }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton size="small" aria-label="검색" onClick={submitKeyword} edge="end">
                <SearchIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          }
        />
        <Select
          size="small"
          displayEmpty
          value={query.workplace ?? ""}
          onChange={(e: SelectChangeEvent) => patch({ workplace: e.target.value })}
          renderValue={(v) => <Prefixed label="근무지" value={v ? String(v) : "전체"} />}
          sx={control}
          MenuProps={menuProps}
        >
          <MenuItem value="">
            <span>전체</span>
            <Count>{summary?.active ?? ""}</Count>
          </MenuItem>
          {workplacesByCount.map((w) => (
            <MenuItem key={w.workplace} value={w.workplace}>
              <span>{w.workplace || "(없음)"}</span>
              <Count>{w.count}</Count>
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={query.status ?? "ACTIVE"}
          onChange={(e: SelectChangeEvent) => patch({ status: e.target.value as AdminEmployeeStatus })}
          renderValue={(v) => <Prefixed label="상태" value={STATUS_LABEL[v as AdminEmployeeStatus]} />}
          sx={control}
          MenuProps={menuProps}
        >
          {(Object.keys(STATUS_LABEL) as AdminEmployeeStatus[]).map((s) => (
            <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={query.managed ?? "ALL"}
          onChange={(e: SelectChangeEvent) => patch({ managed: e.target.value as AdminEmployeeManaged })}
          renderValue={(v) => <Prefixed label="관리" value={MANAGED_LABEL[v as AdminEmployeeManaged]} />}
          sx={control}
          MenuProps={menuProps}
        >
          {(Object.keys(MANAGED_LABEL) as AdminEmployeeManaged[]).map((m) => (
            <MenuItem key={m} value={m}>{MANAGED_LABEL[m]}</MenuItem>
          ))}
        </Select>
        <div style={{ flex: 1 }} />
        <GreenButton onClick={openAdd}>지사 직원 추가</GreenButton>
      </Row>

      {/* 확인이 필요한 직원 — 클릭하면 그 직원만 본다 */}
      <Row sx={{ gap: 0.75, mb: 1.5, justifyContent: "flex-start" }}>
        <Muted style={{ marginRight: 4 }}>확인 필요</Muted>
        {(Object.keys(FLAG_LABEL) as AdminEmployeeFlag[]).map((f) => {
          const count =
            f === "MISSING_IN_SOURCE" ? summary?.flags.missingInSource
            : f === "TYPE_MISMATCH" ? summary?.flags.typeMismatch
            : summary?.flags.noJobPosition;
          const on = query.flag === f;
          const tone = FLAG_TONE[f];
          return (
            <Tooltip key={f} title={FLAG_HELP[f]}>
              <Chip
                size="small"
                clickable
                onClick={() => toggleFlag(f)}
                label={
                  <span>
                    {FLAG_LABEL[f]} <span style={{ fontWeight: 500 }}>{count ?? "–"}</span>
                  </span>
                }
                sx={{
                  height: 26,
                  fontSize: 12,
                  color: tone.fg,
                  backgroundColor: tone.bg,
                  border: `1px solid ${on ? tone.fg : "transparent"}`,
                  "&:hover": { backgroundColor: tone.bg, opacity: 0.85 },
                }}
              />
            </Tooltip>
          );
        })}
        {query.flag && (
          <Muted style={{ marginLeft: 4, cursor: "pointer", textDecoration: "underline" }} onClick={() => patch({ flag: undefined })}>
            전체 보기
          </Muted>
        )}
      </Row>

      {/* 표 — 한 행 한 줄, 색은 상태 점만 */}
      <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${GRAYPALEBORDER}` }}>
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "13%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "13%" }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <Head field="name" label="이름" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <HeadCell>이메일</HeadCell>
              <Head field="workplace" label="근무지" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <HeadCell>소속</HeadCell>
              <Head field="jobPosition" label="직급" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <HeadCell>관리</HeadCell>
              <HeadCell>상태</HeadCell>
              <HeadCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {page.content.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: GRAYPALECONTENTS, py: 4 }}>
                  조건에 맞는 직원이 없습니다
                </TableCell>
              </TableRow>
            )}
            {page.content.map((row) => (
              <TableRow key={row.id} hover>
                <Cell sx={{ fontWeight: 500 }}>
                  <Tooltip title={`마지막 갱신 ${formatDateTime(row.updatedAt) || "—"}${row.employeeId ? ` · 사번 ${row.employeeId}` : ""}`}>
                    <span>{row.name}</span>
                  </Tooltip>
                </Cell>
                <Cell muted>{row.email}</Cell>
                <Cell>{row.workplace}</Cell>
                <Cell muted>{[row.group, row.department, row.team].filter(Boolean).join(" · ") || "—"}</Cell>
                <Cell>{row.jobPosition || "—"}</Cell>
                <Cell muted>
                  <Tooltip
                    title={row.managed
                      ? "인사 명단(portal)으로 매일 자동 갱신됩니다. 여기서 고친 값은 다음날 덮입니다"
                      : "관리자가 직접 관리합니다. 동기화가 건드리지 않습니다"}
                  >
                    <span>{row.managed ? "자동" : "수동"}</span>
                  </Tooltip>
                </Cell>
                <Cell><Status row={row} /></Cell>
                <Cell align="right" sx={{ pr: 1, overflow: "visible" }}>
                  {row.isResignation ? (
                    <RowButton tone="gray" disabled>퇴사</RowButton>
                  ) : row.active ? (
                    <RowButton tone="red" onClick={() => toggleActive(row)}>비활성화</RowButton>
                  ) : (
                    <RowButton tone="green" onClick={() => toggleActive(row)}>활성화</RowButton>
                  )}
                  <Tooltip title={row.managed ? "자동 관리 직원은 portal 인사 명단에서 수정합니다" : ""}>
                    <span>
                      <RowButton tone="yellow" disabled={row.managed} onClick={() => openEdit(row)}>수정</RowButton>
                    </span>
                  </Tooltip>
                </Cell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={page.totalElements}
          page={query.page}
          rowsPerPage={query.size}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_, p) => setQuery((q) => ({ ...q, page: p }))}
          onRowsPerPageChange={(e) => setQuery((q) => ({ ...q, size: Number(e.target.value), page: 0 }))}
          labelRowsPerPage="페이지당"
          labelDisplayedRows={({ from, to, count }) => `${count}명 중 ${from}–${to}`}
          sx={{ color: GRAYPALECONTENTS, fontSize: 12 }}
        />
      </TableContainer>

      <Modal open={open} onClose={handleClose}>
        <Box sx={ModalStyle}>
          <AddEmployee
            employeeData={employeeData}
            setEmployeeData={setEmployeeData}
            modalType={modalType}
            handleClose={handleClose}
            refreshEmployeeData={refresh}
          />
        </Box>
      </Modal>
    </Grid>
  );
}

export default EmployeeList;

// ── 작은 부품들 ───────────────────────────────────────

function Status({ row }: { row: AdminEmployee }) {
  if (row.isResignation) return <Dot color={REDTITLE} label="퇴사" />;
  if (row.flags.includes("MISSING_IN_SOURCE")) {
    return (
      <Tooltip title={FLAG_HELP.MISSING_IN_SOURCE}>
        <span><Dot color={ORANGECONTENTS} label="인사 명단에 없음" /></span>
      </Tooltip>
    );
  }
  if (!row.active) return <Dot color={GRAYPALEDISABLED} label="비활성" />;
  if (row.flags.includes("TYPE_MISMATCH")) {
    return (
      <Tooltip title={FLAG_HELP.TYPE_MISMATCH}>
        <span><Dot color={ORANGECONTENTS} label="소속 확인" /></span>
      </Tooltip>
    );
  }
  return <Dot color={GREENCONTENTS} label="활성" />;
}

function Dot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <span
        style={{
          display: "inline-block", width: 7, height: 7, borderRadius: "50%",
          backgroundColor: color, marginRight: 6, verticalAlign: 1,
        }}
      />
      {label}
    </span>
  );
}

interface HeadProps {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDir: "asc" | "desc";
  onSort: (f: SortField) => void;
}

function Head({ field, label, sortField, sortDir, onSort }: HeadProps) {
  return (
    <HeadCell>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : "asc"}
        onClick={() => onSort(field)}
      >
        {label}
      </TableSortLabel>
    </HeadCell>
  );
}

const control = {
  height: 34,
  fontSize: 13,
  borderRadius: "8px",
  backgroundColor: "#fff",
  "& fieldset": { borderColor: GRAYPALEBORDER },
  "&:hover fieldset": { borderColor: `${GRAYPALEDISABLED} !important` },
  "&.Mui-focused fieldset": { borderColor: `${PRIMARY} !important`, borderWidth: 1 },
  "& .MuiSelect-select": { paddingTop: 0, paddingBottom: 0, display: "flex", alignItems: "center" },
  "& .MuiSelect-icon": { color: GRAYPALECONTENTS },
};

const menuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      borderRadius: "8px",
      border: `1px solid ${GRAYPALEBORDER}`,
      boxShadow: "0 6px 20px rgba(24, 32, 49, 0.08)",
      "& .MuiMenuItem-root": {
        fontSize: 13,
        display: "flex",
        justifyContent: "space-between",
        gap: 3,
        minWidth: 170,
      },
      "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "#FBF0F2", color: PRIMARY },
    },
  },
};

/** 드롭다운 표시값: 회색 라벨 + 진한 값. "근무지 본사" 처럼 읽힌다 */
function Prefixed({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: GRAYPALECONTENTS, fontSize: 12 }}>{label}</span>
      <span style={{ color: GRAYPALETITLE, fontWeight: 500 }}>{value}</span>
    </span>
  );
}

const Count = styled("span")({
  fontSize: 12,
  color: GRAYPALECONTENTS,
  fontVariantNumeric: "tabular-nums",
});

const rowTone = {
  red: { bg: "#FFF0F3", fg: "#F25C75", hover: "#FFE6EB" },
  green: { bg: "#F0FAF7", fg: "#47B998", hover: "#E4F7F1" },
  yellow: { bg: "#FFF9C4", fg: "#F59210", hover: "#FFF59D" },
  gray: { bg: GRAYPALEBACKGROUND1, fg: GRAYPALEDISABLED, hover: GRAYPALEBACKGROUND1 },
};

/** 행 끝 버튼. 다른 관리자 화면의 CustomButton 색과 같고, 폭을 고정해 행마다 줄이 맞는다 */
const RowButton = styled("button", { shouldForwardProp: (p) => p !== "tone" })<{ tone: keyof typeof rowTone }>(
  ({ tone }) => ({
    fontFamily: "Noto sans CJK KR",
    width: 60,
    height: 28,
    marginLeft: 4,
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 5,
    border: 0,
    cursor: "pointer",
    whiteSpace: "nowrap",
    backgroundColor: rowTone[tone].bg,
    color: rowTone[tone].fg,
    "&:hover": { backgroundColor: rowTone[tone].hover },
    "&:disabled": { backgroundColor: GRAYPALEBACKGROUND1, color: GRAYPALEDISABLED, cursor: "default" },
  })
);

const Row = styled(Grid)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
});

const Muted = styled("div")({
  fontSize: 12,
  color: GRAYPALECONTENTS,
  whiteSpace: "nowrap",
});

const HeadCell = styled(TableCell)({
  color: GRAYPALECONTENTS,
  fontWeight: 500,
  fontSize: 12,
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${GRAYPALEBORDER}`,
  paddingTop: 6,
  paddingBottom: 6,
});

const Cell = styled(TableCell, { shouldForwardProp: (p) => p !== "muted" })<{ muted?: boolean }>(
  ({ muted }) => ({
    fontSize: 13,
    color: muted ? GRAYPALECONTENTS : GRAYPALETITLE,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    paddingTop: 8,
    paddingBottom: 8,
  })
);

const Group = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 10,
});

const baseButton = {
  fontFamily: "Noto sans CJK KR",
  height: 34,
  padding: "0 16px",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: 5,
  cursor: "pointer",
  whiteSpace: "nowrap" as const,
};

/** 다른 관리자 화면의 CustomButtonGreen 과 같은 색, 높이만 필터와 맞춘 것 */
const GreenButton = styled("button")({
  ...baseButton,
  backgroundColor: "#F0FAF7",
  color: GREENCONTENTS,
  border: "0px",
  "&:hover": { backgroundColor: "#E4F7F1" },
});

const WhiteButton = styled("button")({
  ...baseButton,
  backgroundColor: "#fff",
  color: GRAYPALETITLE,
  border: `1px solid ${GRAYPALEBORDER}`,
  "&:hover": { backgroundColor: GRAYPALEBACKGROUND1 },
});
